/**
 * @fileoverview Configurações e opções do menu de navegação.
 * Implementa o padrão Command para gerenciar ações do menu e o padrão Composite
 * para estrutura hierárquica de menus.
 *
 * @module MenuOptions
 * @since 1.0.0
 */

/**
 * Enumeração dos tipos de eventos do menu.
 * Define constantes para todas as ações possíveis no sistema de navegação.
 *
 * @enum {string}
 */
export const EventType = {
  NAV_HOME: 'NAV_HOME',
  NAV_TODOS_PRODUTOS: 'NAV_TODOS_PRODUTOS',
  NAV_PRODUTOS_CATEGORIA: 'NAV_PRODUTOS_CATEGORIA',
  NAV_PRODUTOS_MARCA: 'NAV_PRODUTOS_MARCA',
  NAV_PRODUTOS_CADASTRAR_PROMOCAO: 'NAV_PRODUTOS_CADASTRAR_PROMOCAO',
  NAV_PROMOCOES: 'NAV_PROMOCOES',
  NAV_PRODUTOS_PERFUMARIA: 'NAV_PRODUTOS_PERFUMARIA',
  NAV_CLIENTES_LISTAR: 'NAV_CLIENTES_LISTAR',
  NAV_CLIENTES_CADASTRAR: 'NAV_CLIENTES_CADASTRAR',
  NAV_FAVORITOS: 'NAV_FAVORITIES',
  NAV_CONFIG_USUARIOS: 'NAV_CONFIG_USUARIOS',
  NAV_CONFIG_PERMISSOES: 'NAV_CONFIG_PERMISSOES',
  NAV_USUARIOS_GERENCIAR: 'NAV_USUARIOS_GERENCIAR',
  NAV_ROLES_GERENCIAR: 'NAV_ROLES_GERENCIAR',
  NAV_CATEGORIAS_GERENCIAR: 'NAV_CATEGORIAS_GERENCIAR',
  NAV_MARCAS_GERENCIAR: 'NAV_MARCAS_GERENCIAR',
  NAV_PRODUTOS_GERENCIAR: 'NAV_PRODUTOS_GERENCIAR',
  NAV_LANDPAGE_GERENCIAR: 'NAV_LANDPAGE_GERENCIAR',
  NAV_HIPER_PRODUCTS: 'NAV_HIPER_PRODUCTS_SYNC',
  ACC_DEFAULT: 'ACC_DEFAULT',
  ACC_LARGE_TEXT: 'ACC_LARGE_TEXT',
  ACC_BLACK_WHITE: 'ACC_BLACK_WHITE',
  ACC_HIGHLIGHT_TEXT: 'ACC_HIGHLIGHT_TEXT',
};

/**
 * Estrutura de dados do menu principal.
 * Implementa o padrão Composite para criar uma hierarquia de menus
 * com suporte a submenus aninhados.
 *
 * @type {Object}
 * @property {Array<MenuItem>} menu - Array de itens do menu principal
 */
export const menuData = {
  menu: [
    {
      label: 'Início',
      icon: 'home',
      action: EventType.NAV_HOME,
      accessRole: ['All'],
    },
    {
      label: 'Gerenciamento',
      icon: 'settings',
      accessRole: ['admin'],
      submenu: [
        {
          label: 'Gerenciamento de Categorias',
          icon: 'category',
          action: EventType.NAV_CATEGORIAS_GERENCIAR,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Landpage',
          icon: 'store',
          action: EventType.NAV_LANDPAGE_GERENCIAR,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Marcas',
          icon: 'brand',
          action: EventType.NAV_MARCAS_GERENCIAR,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Papéis (Roles)',
          icon: 'role',
          action: EventType.NAV_ROLES_GERENCIAR,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Produtos',
          icon: 'product',
          action: EventType.NAV_PRODUTOS_GERENCIAR,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Promoções',
          icon: 'sell',
          action: EventType.NAV_PRODUTOS_CADASTRAR_PROMOCAO,
          accessRole: ['admin'],
        },
        {
          label: 'Gerenciamento de Usuários',
          icon: 'person',
          action: EventType.NAV_CLIENTES_CADASTRAR,
          accessRole: ['All'],
        },
        {
          label: 'Sincronizar Produtos com HIPER',
          icon: 'sync',
          action: EventType.NAV_HIPER_PRODUCTS,
          accessRole: ['admin'],
        },
      ],
    },
    {
      label: 'Produtos',
      icon: 'cases',
      accessRole: ['All'],
      submenu: [
        {
          label: 'Categorias',
          icon: 'category',
          action: EventType.NAV_PRODUTOS_CATEGORIA,
          accessRole: ['All'],
        },
        {
          label: 'Marcas',
          icon: 'brand',
          action: EventType.NAV_PRODUTOS_MARCA,
          accessRole: ['All'],
        },
        {
          label: 'Todos os Produtos',
          icon: 'product',
          action: EventType.NAV_TODOS_PRODUTOS,
          accessRole: ['All'],
        }
      ],
    },
    {
      label: 'Promoções',
      icon: 'sell',
      action: EventType.NAV_PROMOCOES,
      accessRole: ['All'],
    },
    {
      label: 'Favoritos',
      icon: 'favorite',
      action: EventType.NAV_FAVORITOS,
      accessRole: ['All'],
    },
    {
      label: 'Acessibilidade',
      icon: 'accessibility',
      accessRole: ['All'],
      submenu: [
        {
          label: 'Exibição Padrão',
          action: EventType.ACC_DEFAULT,
          accessRole: ['All'],
        },
        {
          label: 'Aumentar Tamanho Texto',
          action: EventType.ACC_LARGE_TEXT,
          accessRole: ['All'],
        },
        {
          label: 'Tela em Preto e Branco',
          action: EventType.ACC_BLACK_WHITE,
          accessRole: ['All'],
        },
        {
          label: 'Realçar Texto',
          action: EventType.ACC_HIGHLIGHT_TEXT,
          accessRole: ['All'],
        },
      ],
    },
  ],
};

/**
 * Implementação do padrão Command para ações do menu.
 * Mapeia cada tipo de evento para sua respectiva função de execução.
 *
 * @type {Object<string, Function>}
 * @private
 */
const commands = {
  [EventType.NAV_HOME]: ctx => {
    if (ctx.showAllProducts) ctx.showAllProducts();
    if (ctx.clearSearch) ctx.clearSearch();
  },
  [EventType.NAV_TODOS_PRODUTOS]: ctx => {
    if (ctx.showAllProductsGrid) ctx.showAllProductsGrid();
    if (ctx.clearSearch) ctx.clearSearch();
  },
  [EventType.NAV_PRODUTOS_CATEGORIA]: ctx => {
    if (ctx.showCategories) ctx.showCategories();
  },
  [EventType.NAV_PRODUTOS_MARCA]: ctx => {
    if (ctx.showBrands) ctx.showBrands();
  },
  [EventType.NAV_PROMOCOES]: ctx => {
    if (ctx.showPromotions) ctx.showPromotions();
  },
  [EventType.NAV_PRODUTOS_COSMETICOS_MAQUIAGEM]: ctx =>
    ctx.navigate('/produtos/cosmeticos/maquiagem'),
  [EventType.NAV_PRODUTOS_COSMETICOS_CABELOS]: ctx => ctx.navigate('/produtos/cosmeticos/cabelos'),
  [EventType.NAV_PRODUTOS_PERFUMARIA]: ctx => ctx.navigate('/produtos/perfumaria'),
  [EventType.NAV_CLIENTES_LISTAR]: ctx => ctx.navigate('/clientes/listar'),
  [EventType.NAV_CLIENTES_CADASTRAR]: ctx => {
    if (ctx.openUserManager) ctx.openUserManager();
  },
  [EventType.NAV_FAVORITOS]: ctx => {
    if (ctx.showFavorites) ctx.showFavorites();
  },
  [EventType.NAV_CONFIG_USUARIOS]: ctx => ctx.navigate('/config/usuarios'),
  [EventType.NAV_CONFIG_PERMISSOES]: ctx => ctx.navigate('/config/permissoes'),
  [EventType.NAV_USUARIOS_GERENCIAR]: ctx => {
    if (ctx.openUserManager) ctx.openUserManager();
  },
  [EventType.NAV_ROLES_GERENCIAR]: ctx => {
    if (ctx.openRoleManager) ctx.openRoleManager();
  },
  [EventType.NAV_PRODUTOS_CADASTRAR_PROMOCAO]: ctx => {
    if (ctx.openPromotionManager) ctx.openPromotionManager();
  },
  [EventType.NAV_CATEGORIAS_GERENCIAR]: ctx => {
    if (ctx.openCategoryManager) ctx.openCategoryManager();
  },
  [EventType.NAV_MARCAS_GERENCIAR]: ctx => {
    if (ctx.openBrandManager) ctx.openBrandManager();
  },
  [EventType.NAV_PRODUTOS_GERENCIAR]: ctx => {
    if (ctx.openProductManager) ctx.openProductManager();
  },
  [EventType.NAV_LANDPAGE_GERENCIAR]: ctx => {
    if (ctx.openLandpageManager) ctx.openLandpageManager();
  },
  [EventType.NAV_HIPER_PRODUCTS]: ctx => {
    if (ctx.openHiperSync) ctx.openHiperSync();
  },
  [EventType.ACC_DEFAULT]: ctx => ctx.setAccessibility({ mode: 'default' }),
  [EventType.ACC_LARGE_TEXT]: ctx => ctx.setAccessibility({ mode: 'largeText' }),
  [EventType.ACC_BLACK_WHITE]: ctx => ctx.setAccessibility({ mode: 'blackWhite' }),
  [EventType.ACC_HIGHLIGHT_TEXT]: ctx => ctx.setAccessibility({ mode: 'highlightText' }),
};

/**
 * Manipulador de comandos do menu.
 * Executa a ação correspondente ao tipo de evento fornecido.
 *
 * @param {string} eventType - Tipo do evento a ser executado
 * @param {Object} ctx - Contexto com funções e dados necessários
 * @param {Function} ctx.navigate - Função de navegação
 * @param {Function} ctx.setAccessibility - Função para alterar acessibilidade
 * @param {Function} ctx.showFavorites - Função para mostrar favoritos
 * @param {Function} ctx.showAllProducts - Função para mostrar todos os produtos
 * @param {Function} ctx.showCategories - Função para mostrar categorias
 * @param {Function} ctx.showPromotions - Função para mostrar promoções
 * @param {Function} ctx.clearSearch - Função para limpar busca
 * @param {Function} ctx.openUserManager - Função para abrir modal de gerenciamento de usuários
 * @returns {*} Resultado da execução do comando
 */
export function handle(eventType, ctx) {
  return commands[eventType]?.(ctx);
}
